// Admins pick a bride/groom family; requests from family service pages then carry
// the family's id so the API returns and edits that family's data only.

const STORAGE_KEY = 'selectedFamily';
export const FAMILY_HEADER = 'X-Family-User-Id';

// Pages that are never family-scoped: the admin's own pages, the Service Wizard (it
// has its own bride/groom client picker, incl. joint wishlists), Contracts (an
// admin must not sign/accept on a family's behalf; admins use Admin > Contracts)
// and the vendor marketplace pages (not family data).
const PERSONAL_PATHS = [
  '/dashboard/overview', // each role has its own dashboard (an admin sees the admin one)
  '/dashboard/profile',
  '/dashboard/matchmaking',
  '/dashboard/messages',
  '/dashboard/meetings',
  '/dashboard/wishlist-wizard',
  '/dashboard/contracts',
  '/dashboard/service-providers',
  '/dashboard/my-services',
  '/dashboard/vendor-bookings',
  '/dashboard/notifications', // everyone's own inbox (matches the notification bell)
];

// Admin pages listing every family's records that narrow to the selected bride/groom family
// (records added there are saved against it). A family is optional on these pages.
const FAMILY_ADMIN_PATHS = [
  '/dashboard/admin/staffing', '/dashboard/admin/transportation', '/dashboard/admin/parking',
  '/dashboard/admin/budget-categories',
];

export const isFamilyAdminPath = (pathname) => FAMILY_ADMIN_PATHS.some(p => pathname.startsWith(p));

// Operations that must always run as the logged-in user
const UNSCOPED_OPERATIONS = new Set(['Me', 'Login', 'Signup', 'FamilyUsers', 'MyUnreadNotifications']);

// Operations that act for the selected family on any page (e.g. booking a vendor from
// Service Providers, which is otherwise not a family page, and listing only the vendors
// for the services on that family's signed contract)
const FAMILY_OPERATIONS = new Set(['CreateVendorSelection', 'MyVendorSelections', 'MyBookableServices', 'FamilyVendorOfferings']);

export function getSelectedFamily() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null;
  } catch {
    return null;
  }
}

export function setSelectedFamily(family) {
  try {
    if (family) localStorage.setItem(STORAGE_KEY, JSON.stringify(family));
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* storage unavailable */ }
}

export function isFamilyScopedPath(pathname) {
  if (isFamilyAdminPath(pathname)) return true;
  return pathname.startsWith('/dashboard/')
    && !pathname.startsWith('/dashboard/admin')
    && !PERSONAL_PATHS.some(p => pathname.startsWith(p));
}

// Pages where an admin must pick a family before any data is shown or entered
export function requiresFamilySelection(pathname) {
  return isFamilyScopedPath(pathname) && !isFamilyAdminPath(pathname);
}

// Header to add to a GraphQL request, or null
export function familyHeaderFor(operationName, pathname) {
  const family = getSelectedFamily();
  if (!family?.id || UNSCOPED_OPERATIONS.has(operationName)) return null;
  if (FAMILY_OPERATIONS.has(operationName)) return family.id;
  return isFamilyScopedPath(pathname) ? family.id : null;
}
